import {
  CorrectSchedulingBookingContactRequestSchema,
  DemoAvailabilityResponseSchema,
  DemoScheduleBlockSchema,
  type DemoSchedulingConfiguration,
  IssuePublicBookingTokensRequestSchema,
  IssuePublicBookingTokensResponseSchema,
  IssueVisitIntakeLinkRequestSchema,
  SchedulingAppointmentSchema,
  SchedulingSaveResultSchema,
  type SchedulingSettings,
  SchedulingStateSchema,
  type UpdateSchedulingSettingsRequest,
  VisitIntakeLinkResponseSchema,
} from "@emr/contracts";
import { z } from "zod";
import type { DemoSchedulingClient } from "./demoSchedulingClient";
import { schedulingRequestError } from "./schedulingErrors";

const receiptSchema = z.object({ appointment: SchedulingAppointmentSchema });

/** A settings save carries no physician calendar: each calendar is saved, and versioned, alone. */
function schedulingSettings(configuration: DemoSchedulingConfiguration): SchedulingSettings {
  const {
    providerAvailability: _rules,
    availabilityOverrides: _overrides,
    providers,
    ...settings
  } = configuration;
  return {
    ...settings,
    providers: providers.map(({ availabilityVersion: _calendarVersion, ...provider }) => provider),
  };
}

/** The existing screens share a presentation shape, but all writes use durable versions. */
export function createPersistedSchedulingClient(options: {
  readonly baseUrl: string;
  readonly siteId: string;
  readonly getAccessToken: () => Promise<string>;
}): DemoSchedulingClient {
  const expectedVersion = (version: number | undefined) =>
    z.number().int().positive().parse(version);
  async function request(path: string, body?: unknown, signal?: AbortSignal): Promise<unknown> {
    const token = await options.getAccessToken();
    const response = await fetch(`${options.baseUrl.replace(/\/$/, "")}/v1/scheduling${path}`, {
      method: body === undefined ? "GET" : "POST",
      headers: {
        authorization: `Bearer ${token}`,
        "x-site-id": options.siteId,
        ...(body === undefined ? {} : { "content-type": "application/json" }),
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      ...(signal === undefined ? {} : { signal }),
      cache: "no-store",
    });
    if (!response.ok) {
      throw await schedulingRequestError(
        response,
        "The clinic schedule is unavailable. Please refresh.",
      );
    }
    return response.json();
  }
  return {
    async issuePublicTokens(id, version, issueOptions) {
      // patientDetails is sent only when asked, so a plain re-issue reads as before PA-10; the
      // opened link it replaces only once the desk confirmed.
      const body = IssuePublicBookingTokensRequestSchema.parse({
        expectedVersion: expectedVersion(version),
        ...(issueOptions?.patientDetails === true ? { patientDetails: true } : {}),
        ...(issueOptions?.patientDetails === true && issueOptions.replaceOpenedLinkId !== undefined
          ? { replaceOpenedLinkId: issueOptions.replaceOpenedLinkId }
          : {}),
      });
      return IssuePublicBookingTokensResponseSchema.parse(
        await request(`/appointments/${encodeURIComponent(id)}/tokens`, body),
      );
    },
    async revokePublicTokens(id, version) {
      await request(`/appointments/${encodeURIComponent(id)}/tokens/revoke`, {
        expectedVersion: expectedVersion(version),
      });
    },
    async issueVisitIntakeLink(encounterId, input, signal) {
      // Encounter ids are uuids; anything else never reaches a URL.
      const id = z.string().uuid().parse(encounterId);
      return VisitIntakeLinkResponseSchema.parse(
        await request(
          `/visits/${encodeURIComponent(id)}/intake-link`,
          IssueVisitIntakeLinkRequestSchema.parse(input),
          signal,
        ),
      );
    },
    async correctBookingContact(id, input, signal) {
      // Appointment ids are uuids; anything else never reaches a URL.
      const appointmentId = z.string().uuid().parse(id);
      return SchedulingAppointmentSchema.parse(
        await request(
          `/appointments/${encodeURIComponent(appointmentId)}/contact`,
          CorrectSchedulingBookingContactRequestSchema.parse(input),
          signal,
        ),
      );
    },
    async getState(signal) {
      return SchedulingStateSchema.parse(await request("/state", undefined, signal));
    },
    async getAvailability(query, signal) {
      const parameters = new URLSearchParams({
        fromDate: query.fromDate,
        toDate: query.toDate,
        appointmentTypeId: query.appointmentTypeId,
      });
      if (query.providerId) parameters.set("providerId", query.providerId);
      return DemoAvailabilityResponseSchema.parse(
        await request(`/availability?${parameters}`, undefined, signal),
      );
    },
    async createAppointment(input, signal) {
      const receipt = receiptSchema.parse(await request("/appointments", input, signal));
      return { appointment: receipt.appointment, reminders: [] };
    },
    async updateAppointment(id, input, signal) {
      const {
        expectedUpdatedAt: _updatedAt,
        expectedVersion: version,
        encounterId,
        ...patch
      } = input;
      if (encounterId !== undefined) throw new Error("Check-in links the encounter automatically.");
      const appointment = SchedulingAppointmentSchema.parse(
        await request(
          `/appointments/${encodeURIComponent(id)}/update`,
          {
            ...patch,
            expectedVersion: expectedVersion(version),
          },
          signal,
        ),
      );
      return appointment;
    },
    async updateAppointmentStatus(id, input, signal) {
      const appointment = SchedulingAppointmentSchema.parse(
        await request(
          `/appointments/${encodeURIComponent(id)}/status`,
          {
            status: input.status,
            expectedVersion: expectedVersion(input.expectedVersion),
          },
          signal,
        ),
      );
      return appointment;
    },
    async updateConfiguration(configuration, saveOptions, signal) {
      const body: UpdateSchedulingSettingsRequest = {
        expectedVersion: configuration.version,
        configuration: schedulingSettings(configuration),
        ...(saveOptions?.acknowledgementToken === undefined
          ? {}
          : { acknowledgementToken: saveOptions.acknowledgementToken }),
      };
      return SchedulingSaveResultSchema.parse(await request("/configuration", body, signal));
    },
    async saveProviderAvailability(providerId, input, signal) {
      return SchedulingSaveResultSchema.parse(
        await request(`/providers/${encodeURIComponent(providerId)}/availability`, input, signal),
      );
    },
    async createBlock(input, signal) {
      return DemoScheduleBlockSchema.parse(await request("/blocks", input, signal));
    },
    async removeBlock(block, signal) {
      await request(
        `/blocks/${encodeURIComponent(block.id)}/remove`,
        { expectedCreatedAt: block.createdAt },
        signal,
      );
    },
    async sendReminder() {
      throw new Error("Reminder delivery is not configured. No message was sent.");
    },
    async createScheduledIntakeInvitation() {
      throw new Error("Contact the clinic administrator to issue a secure intake link.");
    },
  };
}
